import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { uploadUnitMedia } from '@/api/upload';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import type { AddItemUnitBatchResult } from '@core/api/masters';
import { lookLabel } from '@core/bookings/catalogue';
import { can } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { withReference } from '@core/units/referenceNumber';

import { TagPrint } from './TagPrint';

/**
 * What came in — the phone's received screen (`app/stock/receive.tsx`) on the website: every new
 * code, a photograph of each in place, and the tags to print. A photograph now is the garment's
 * condition on the day it arrived, which is what a damage dispute later turns on.
 *
 * <p>
 * The tags are the core's `hangTagsPage`, the page the phone prints, drawn in a frame and printed
 * from it — so a tag reads the same from either client. Each carries its own rate only for a role
 * that may see money (`ItemUnit:ViewFinancials`).
 * </p>
 */
export function ReceivedStock({
  session,
  result,
  photoFailures,
  photographed,
  onMore,
  onDone,
}: {
  session: Session;
  result: AddItemUnitBatchResult;
  /** Photographs (the garments' own and the designs') that did not send after booking in. */
  photoFailures: number;
  /** Garments whose own photo step 2 already sent. */
  photographed?: ReadonlySet<number>;
  onMore: () => void;
  onDone: () => void;
}) {
  const units = result.lines.flatMap((line) => line.units);
  const mayPrint = can(session, 'ItemUnit:Print');
  const mayPhotograph = can(session, 'ItemUnit:Edit');
  const [taken, setTaken] = useState<ReadonlySet<number>>(() => photographed ?? new Set());
  const upload = useMutation({
    mutationFn: ({ unitId, file }: { unitId: number; file: File }) => uploadUnitMedia(unitId, file),
    onSuccess: (_, { unitId }) => setTaken((current) => new Set([...current, unitId])),
  });

  const designs = [...new Set(units.map((unit) => unit.itemName))];
  const looks = [...new Set(units.map((unit) => lookLabel(unit)).filter((look) => look !== ''))];
  const line =
    designs.length === 1
      ? [designs[0], ...looks, 'on the rail now'].join(' · ')
      : `${designs.length} designs · on the rail now`;

  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      <PageHeading
        title={`${result.totalUnits} garment${result.totalUnits === 1 ? '' : 's'} in`}
        subtitle={result.goodsReceiptNumber ? `${line} · ${result.goodsReceiptNumber}` : line}
        actions={
          <>
            <Button icon="icon icon-plus" onClick={onMore} shortcut="create">
              Book in more
            </Button>
            <Button variant={mayPrint ? 'secondary' : 'primary'} onClick={onDone} shortcut="back">
              Done
            </Button>
          </>
        }
      />

      {photoFailures > 0 && (
        <Notice tone="warning" title="Some photos did not send">
          {`${photoFailures} photo${photoFailures === 1 ? '' : 's'} did not send. The stock is in — add ${photoFailures === 1 ? 'it' : 'them'} from the garment or the design.`}
        </Notice>
      )}

      <div className="grid grid-cols-12 gap-3 lg:gap-4">
        <div className="col-span-12 xl:col-span-7">
          <Card>
            <p className="text-sm font-semibold text-gray-900">The new codes</p>
            {mayPhotograph && (
              <p className="text-default mb-2 text-[length:var(--scale-list-meta)]">
                A photo of each one shows its real condition, and helps if a customer disputes
                damage later.
              </p>
            )}
            {upload.isError && (
              <Notice tone="danger" title="That photo did not send">
                {upload.error instanceof Error ? upload.error.message : 'Try it again.'}
              </Notice>
            )}
            <ul className="ps-0">
              {units.map((unit, index) => {
                const done = taken.has(unit.id);
                const sending = upload.isPending && upload.variables?.unitId === unit.id;

                return (
                  <li
                    key={unit.id}
                    className={`flex list-none items-center gap-3 py-2.5 ${
                      index > 0 ? 'border-border-color border-t' : ''
                    }`}
                  >
                    <span className="bg-light flex h-11 w-9 shrink-0 items-center justify-center rounded">
                      {done && <i className="icon icon-check text-success" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="font-mono-nums block text-[15px] font-bold text-gray-900">
                        {withReference(unit.serialCode, unit.referenceNumber)}
                      </span>
                      <span className="text-default block truncate text-[length:var(--scale-list-meta)]">
                        {unit.variantDisplayName}
                      </span>
                    </span>
                    {done ? (
                      <span className="text-[13px] font-semibold text-gray-900">Photo added</span>
                    ) : mayPhotograph ? (
                      <label
                        className={`border-border-color hover:bg-light flex cursor-pointer items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-900 ${
                          sending ? 'pointer-events-none opacity-50' : ''
                        }`}
                      >
                        <i className="icon icon-camera" />
                        {sending ? 'Sending…' : 'Photo'}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          aria-label={`Photograph ${unit.serialCode}`}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';

                            if (file) {
                              upload.reset();
                              upload.mutate({ unitId: unit.id, file });
                            }
                          }}
                        />
                      </label>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {mayPrint && (
          <div className="col-span-12 xl:col-span-5">
            <TagPrint session={session} units={units} />
          </div>
        )}
      </div>
    </div>
  );
}
